import { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

interface Notification {
  id: string;
  user_id: string;
  type: string;
  titre: string;
  message: string;
  lien?: string;
  lu: boolean;
  date_creation: string;
  metadata?: any;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  loading: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Son de notification discret
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(800, ctx.currentTime);
    osc.frequency.setValueAtTime(1000, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.25);
    setTimeout(() => ctx.close(), 400);
  } catch (_) { /* audio non supporté */ }
}

// BroadcastChannel pour dédupliquer les notifications Realtime entre onglets
// Un seul onglet joue le son et affiche la notification navigateur.
const NOTIFICATION_CHANNEL = typeof BroadcastChannel !== 'undefined'
  ? new BroadcastChannel('csf-notification-dedup')
  : null;

// Timestamp de la dernière notification reçue (pour éviter les doublons Realtime + Push)
let lastNotificationTimestamp = 0;

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { user, passwordRequirement, role } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const isSubscribedRef = useRef(false);

  // Charger les notifications
  const loadNotifications = async () => {
    if (!user) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('date_creation', { ascending: false })
      .limit(50);

    if (!error && data) {
      setNotifications(data);
    }
    setLoading(false);
  };

  // Fonction de souscription Realtime — appelée au démarrage
  // et ré-appelée au retour en ligne pour rattraper les événements manqués
  const subscribeToRealtime = () => {
    if (!user || isSubscribedRef.current) return;

    // Nettoyer l'ancienne souscription si existante
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    // S'abonner aux changements en temps réel
    const channel = supabase
      .channel('notifications-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          const notifDate = payload.new?.date_creation
            ? new Date(payload.new.date_creation).getTime()
            : Date.now();

          if (payload.eventType === 'INSERT') {
            setNotifications((prev) => {
              // Éviter les doublons (même ID déjà présent)
              if (prev.some((n) => n.id === payload.new.id)) return prev;
              return [payload.new as Notification, ...prev];
            });

            // Déduplication entre onglets via BroadcastChannel
            if (notifDate > lastNotificationTimestamp) {
              lastNotificationTimestamp = notifDate;

              // Informer les autres onglets qu'on a déjà traité cette notif
              NOTIFICATION_CHANNEL?.postMessage({
                type: 'NOTIFICATION_PLAYED',
                notificationId: payload.new.id,
                timestamp: notifDate,
              });

              // Jouer le son
              playNotificationSound();

              // Notification navigateur si permission accordée
              if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                const notif = payload.new as Notification;
                new Notification(notif.titre, {
                  body: notif.message,
                  icon: '/favicon.ico',
                  badge: '/favicon.ico',
                  tag: `notif-${notif.id}`,
                });
              }
            }
          } else if (payload.eventType === 'UPDATE') {
            setNotifications((prev) =>
              prev.map((n) => (n.id === payload.new.id ? (payload.new as Notification) : n))
            );
          } else if (payload.eventType === 'DELETE') {
            setNotifications((prev) => prev.filter((n) => n.id !== payload.old.id));
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          isSubscribedRef.current = true;
        } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
          isSubscribedRef.current = false;
        }
      });

    channelRef.current = channel;
  };

  // Écouter les autres onglets via BroadcastChannel
  // pour éviter de jouer le son/de lancer la notif navigateur en double
  useEffect(() => {
    if (!NOTIFICATION_CHANNEL) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'NOTIFICATION_PLAYED') {
        lastNotificationTimestamp = event.data.timestamp;
      }
    };

    NOTIFICATION_CHANNEL.addEventListener('message', handleMessage);
    return () => {
      NOTIFICATION_CHANNEL.removeEventListener('message', handleMessage);
    };
  }, []);

  // Souscription Realtime + chargement initial
  useEffect(() => {
    if (!user) {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
        isSubscribedRef.current = false;
      }
      setNotifications([]);
      setLoading(false);
      return;
    }

    loadNotifications();
    subscribeToRealtime();

    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
        isSubscribedRef.current = false;
      }
    };
  }, [user]);

  // Recharger les notifications et resouscrire Realtime au retour en ligne
  // (Supabase Realtime peut avoir perdu des événements pendant la coupure)
  useEffect(() => {
    const handleOnline = () => {
      if (user) {
        loadNotifications();
        // Forcer la re-souscription Realtime après une coupure réseau
        if (channelRef.current) {
          supabase.removeChannel(channelRef.current);
          channelRef.current = null;
          isSubscribedRef.current = false;
        }
        subscribeToRealtime();
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [user]);

  // Créer une notification automatique pour le changement de mot de passe
  useEffect(() => {
    if (!user || role !== 'patient' || !passwordRequirement) return;

    const createPasswordNotification = async () => {
      if (passwordRequirement.mustChangePassword && !passwordRequirement.deadlinePassed) {
        // Vérifier si une notification existe déjà
        const { data: existingNotif } = await supabase
          .from('notifications')
          .select('id')
          .eq('user_id', user.id)
          .eq('type', 'password_change_required')
          .eq('lu', false)
          .maybeSingle();

        if (!existingNotif) {
          // Créer une nouvelle notification
          const deadline = new Date(passwordRequirement.deadline!);
          const now = new Date();
          const hoursRemaining = Math.floor((deadline.getTime() - now.getTime()) / (1000 * 60 * 60));
          const minutesRemaining = Math.floor(((deadline.getTime() - now.getTime()) % (1000 * 60 * 60)) / (1000 * 60));

          const { error } = await supabase.from('notifications').insert({
            user_id: user.id,
            type: 'password_change_required',
            titre: '⚠️ Changement de mot de passe requis',
            message: `Votre compte a été créé par un membre du personnel médical. Vous devez changer votre mot de passe dans les ${hoursRemaining}h ${minutesRemaining}min restantes pour des raisons de sécurité.`,
            lien: '/change-password',
            lu: false,
          });

          if (error) {
            // Silencieux
          } else {
            loadNotifications();
          }
        }
      }
    };

    createPasswordNotification();
  }, [user, role, passwordRequirement]);

  const markAsRead = async (id: string) => {
    const { error } = await supabase
      .from('notifications')
      .update({ lu: true })
      .eq('id', id);

    if (!error) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, lu: true } : n))
      );
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;

    const { error } = await supabase
      .from('notifications')
      .update({ lu: true })
      .eq('user_id', user.id)
      .eq('lu', false);

    if (!error) {
      setNotifications((prev) => prev.map((n) => ({ ...n, lu: true })));
    }
  };

  const deleteNotification = async (id: string) => {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', id);

    if (!error) {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }
  };

  const unreadCount = notifications.filter((n) => !n.lu).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        loading
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}